import { useEffect, useRef, useState, type ReactNode } from "react";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  App,
  Button,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import type { FormInstance } from "antd/es/form";
import BrandButton, { brandModalConfirmProps } from "@/components/BrandButton";
import {
  CountedInput,
  I18nGlobe,
  I18nModal,
  MoneyInput,
  PeriodTags,
  PositiveIntInput,
  SortableCard,
  SortableList,
  UtcDatePicker,
} from "./components";
import {
  BILLING_TYPES,
  BillingType,
  EffectiveMode,
  PERIOD_UNITS,
  PeriodUnit,
  ProductApiCode,
  ProductApiError,
  ProductStatus,
  createProduct,
  getProduct,
  publishProduct,
  updateProduct,
  type I18nText,
  type ProductPeriod,
} from "@/api/products";
import {
  BILLING_I18N,
  I18N_DEFAULT,
  copyI18n,
  defaultI18n,
  isFutureUtc,
  ListMode,
  PERIOD_UNIT_LABEL_I18N,
  periodPhraseKey,
  SAVE_TOAST_KEY,
  SaveToastKey,
  UnlistMode,
  applyBillingSwitch,
  billingLocked,
  canDeletePeriod,
  classifySaveToast,
  defaultForm,
  emptyI18n,
  emptyModule,
  emptyTier,
  formatUsd,
  fromProduct,
  listTimeReadonly,
  moneyOk,
  needsPeriods,
  periodPrice,
  sortPeriods,
  toPayload,
  tryAddPeriod,
  utcWall,
  type FormState,
} from "./rules";
import WarningIcon from "@/assets/waring_alert.svg?react";
import AddIcon from "@/assets/add.svg?react";
import styles from "./index.module.scss";
import { cn } from "@/utils/cn";

export type ProductEditProps = {
  open: boolean;
  /** 在列表页抽屉里新建，不走 /products/new。 */
  creating?: boolean;
  /** 在列表页抽屉里编辑已有产品。 */
  productId?: string;
  onClose?: () => void;
  /** 关闭动画结束后清空编辑对象，避免收起过程中内容被换成新建。 */
  onClosed?: () => void;
  onChanged?: () => void;
};

function ProductEdit({
  open,
  creating = false,
  productId: editingId,
  onClose,
  onClosed,
  onChanged,
}: ProductEditProps) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const { app_id = "", productId: routeProductId } = useParams();
  const navigate = useNavigate();
  const [localId, setLocalId] = useState<string | null>(null);
  const skipReload = useRef(false);
  const productId =
    editingId ?? (creating ? (localId ?? undefined) : routeProductId);
  const isNew = !productId || productId === "new";
  const [form] = Form.useForm<FormState>();
  const [i18nForm] = Form.useForm<I18nText>();

  const [live, setLive] = useState<FormState>(defaultForm());
  const [snapshot, setSnapshot] = useState<FormState>(defaultForm());
  const [status, setStatus] = useState<ProductStatus>(ProductStatus.Draft);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [addingPeriod, setAddingPeriod] = useState(false);
  const [newLen, setNewLen] = useState<number | null>(1);
  const [newUnit, setNewUnit] = useState<PeriodUnit>(PeriodUnit.Month);
  const [i18nField, setI18nField] = useState<null | {
    title: string;
    max: number;
    apply: (next: I18nText) => void;
    emptyMessage?: string;
  }>(null);

  const dirty = JSON.stringify(live) !== JSON.stringify(snapshot);
  const lockedBilling = billingLocked(status);
  const periodRemovable = canDeletePeriod(status, isNew);
  const listReadonly = listTimeReadonly(status);
  const unlistAfterNow = live.listNow || status === ProductStatus.Published;
  const showPeriods = needsPeriods(live.billing_type);
  const sortedPeriods = sortPeriods(live.periods);

  const leaveToList = () => {
    if (onClose) onClose();
    else navigate(`/apps/${app_id}/products`);
  };

  const requestClose = () => {
    if (!onClose) {
      leaveToList();
      return;
    }
    if (!dirty || saving) {
      onClose();
      return;
    }
    modal.confirm({
      title: t("products.leaveTitle"),
      content: t("products.leaveBody"),
      okText: t("products.leaveOk"),
      cancelText: t("products.leaveCancel"),
      ...brandModalConfirmProps("danger"),
      onOk: () => onClose(),
    });
  };

  const blocker = useBlocker(dirty && !saving);

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    modal.confirm({
      title: t("products.leaveTitle"),
      content: t("products.leaveBody"),
      okText: t("products.leaveOk"),
      cancelText: t("products.leaveCancel"),
      ...brandModalConfirmProps("danger"),
      onOk: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
    });
  }, [blocker, modal, t]);

  useEffect(() => {
    if (isNew) return;
    if (skipReload.current) {
      skipReload.current = false;
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getProduct(app_id, productId)
      .then((p) => {
        if (cancelled) return;
        const next = fromProduct(p);
        form.setFieldsValue(next);
        setLive(next);
        setSnapshot(next);
        setStatus(p.status);
      })
      .catch(() => {
        if (!cancelled) {
          message.error(t("products.network"));
          if (onClose) onClose();
          else navigate(-1);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [app_id, form, isNew, onClose, productId, message, navigate, t]);

  const read = (): FormState => ({
    ...defaultForm(),
    ...form.getFieldsValue(true),
  });

  const sync = (next: FormState) => setLive(next);

  const periodTitle = (
    length: number,
    unit: PeriodUnit,
    style: "card" | "head",
  ) => t(periodPhraseKey(length, unit, style), { n: length });

  const store = form as FormInstance;

  const moneyRule = (message: string) => ({
    validator: (_: unknown, value: string) =>
      moneyOk(value ?? "")
        ? Promise.resolve()
        : Promise.reject(new Error(message)),
  });

  const uniqueNameRule = (list: "tiers" | "modules") => ({
    validator: (_: unknown, value: string) => {
      const name = String(value ?? "").trim();
      if (!name) return Promise.resolve();
      const rows = (form.getFieldValue(list) ?? []) as { name?: I18nText }[];
      const duplicated =
        rows.filter((row) => defaultI18n(row?.name).trim() === name).length > 1;
      return duplicated
        ? Promise.reject(new Error(t("products.nameUnique")))
        : Promise.resolve();
    },
  });

  const failValidate = (err: unknown) => {
    const fields = (err as { errorFields?: { errors: string[] }[] })
      .errorFields;
    const msg = fields?.[0]?.errors?.[0];
    if (msg) message.error(msg);
  };

  const persist = async () => {
    const body = toPayload(read());
    if (isNew) return createProduct(app_id, body);
    return updateProduct(app_id, productId, body);
  };

  const fail = (err: unknown, fallback: string) => {
    if (err instanceof ProductApiError && err.code === ProductApiCode.Limit) {
      message.error(t("products.limitTip"));
      return;
    }
    message.error(fallback);
  };

  const onSaveDraft = async () => {
    try {
      await form.validateFields([["name", I18N_DEFAULT]]);
    } catch (err) {
      failValidate(err);
      return;
    }
    const state = read();
    setSaving(true);
    try {
      const saved = await persist();
      message.success(t("products.draftSaved"));
      setSnapshot(state);
      onChanged?.();
      if (isNew) {
        skipReload.current = true;
        setLocalId(saved.id);
      }
    } catch (err) {
      fail(err, t("products.network"));
    } finally {
      setSaving(false);
    }
  };

  const onSavePublished = async () => {
    try {
      await form.validateFields();
    } catch (err) {
      failValidate(err);
      return;
    }
    const state = read();
    setSaving(true);
    try {
      await persist();
      const toast = classifySaveToast(status, isNew, snapshot, state);
      message.success(
        toast.key === SaveToastKey.UnlistSaved
          ? t("products.unlistSaved", { time: toast.time })
          : t(SAVE_TOAST_KEY[toast.key]),
      );
      setSnapshot(state);
    } catch (e) {
      fail(e, t("products.network"));
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    try {
      await form.validateFields();
    } catch (err) {
      failValidate(err);
      return;
    }
    const state = read();
    const future = !state.listNow && state.list_at;
    modal.confirm({
      title: future
        ? t("products.confirmScheduleTitle")
        : t("products.confirmPublishTitle"),
      content: (
        <div className="whitespace-pre-wrap">
          {future
            ? t("products.confirmScheduleBody", {
                time: utcWall(state.list_at),
              })
            : t("products.confirmPublishBody")}
          {state.unlistOn && state.unlist_at
            ? `\n${t("products.confirmUnlistLine", { time: utcWall(state.unlist_at) })}`
            : ""}
        </div>
      ),
      okText: future ? t("products.scheduleAction") : t("products.publish"),
      cancelText: t("common.cancel"),
      ...brandModalConfirmProps("primary"),
      onOk: async () => {
        setSaving(true);
        try {
          const saved = await persist();
          const published = await publishProduct(app_id, saved.id);
          if (
            published.status === ProductStatus.Scheduled &&
            published.list_at
          ) {
            message.success(
              t("products.scheduledOk", { time: utcWall(published.list_at) }),
            );
          } else {
            message.success(t("products.publishedOk"));
          }
          setSnapshot(state);
          if (onClose) {
            onChanged?.();
            onClose();
          } else {
            navigate(`/apps/${app_id}/products`);
          }
        } catch (e) {
          fail(e, t("products.network"));
          throw e;
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const addPeriod = () => {
    if (!newLen || newLen < 1 || !Number.isInteger(newLen)) return;
    const next = tryAddPeriod(live.periods, {
      unit: newUnit,
      length: newLen,
    });
    if (!next) {
      message.error(t("products.periodExists"));
      return;
    }
    form.setFieldValue("periods", next);
    sync({ ...read(), periods: next });
    setAddingPeriod(false);
    setNewLen(1);
    setNewUnit(PeriodUnit.Month);
  };

  const periodCard = (p: ProductPeriod) => {
    const monthly = Number(live.planPrice);
    const showMoney =
      (live.billing_type === BillingType.Workspace ||
        live.billing_type === BillingType.Seat) &&
      moneyOk(live.planPrice);
    return {
      title: periodTitle(p.length, p.unit, "card"),
      price: showMoney ? formatUsd(periodPrice(monthly, p)) : undefined,
    };
  };

  const openI18n = (
    title: string,
    max: number,
    value: I18nText,
    apply: (next: I18nText) => void,
    emptyMessage?: string,
  ) => {
    i18nForm.setFieldsValue(copyI18n(value));
    setI18nField({ title, max, apply, emptyMessage });
  };

  const globe = (
    text: I18nText,
    max: number,
    apply: (next: I18nText) => void,
    title: string,
    emptyMessage?: string,
  ) => (
    <I18nGlobe
      text={text}
      onClick={() => openI18n(title, max, text, apply, emptyMessage)}
    />
  );

  const applyI18n = (path: (string | number)[], next: I18nText) => {
    store.setFieldValue(path, next);
    sync(read());
    void form.validateFields([[...path, I18N_DEFAULT]]).catch(() => undefined);
  };

  const showEffective = live.billing_type !== BillingType.Free;
  const title =
    creating || isNew ? t("products.create") : t("products.editTitle");

  return (
    <Drawer
      open={open}
      size={750}
      destroyOnHidden
      className={styles.drawer}
      title={
        <div className="flex items-center justify-between pr-8">
          <span>{title}</span>
          {loading ? null : status === ProductStatus.Draft || isNew ? (
            <Space>
              <BrandButton
                variant="outline"
                loading={saving}
                onClick={() => void onSaveDraft()}
              >
                {t("products.saveDraft")}
              </BrandButton>
              <BrandButton loading={saving} onClick={() => void doPublish()}>
                {t("products.submitPublish")}
              </BrandButton>
            </Space>
          ) : (
            <BrandButton
              loading={saving}
              onClick={() => void onSavePublished()}
            >
              {t("products.save")}
            </BrandButton>
          )}
        </div>
      }
      onClose={requestClose}
      afterOpenChange={(visible) => {
        if (!visible) onClosed?.();
      }}
    >
      {loading ? (
        <div className="p-8 text-secondary">{t("common.loading")}</div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={defaultForm()}
          className={styles.form}
          validateTrigger="onBlur"
          scrollToFirstError={{ block: "center" }}
          onValuesChange={(changed) => {
            if (changed.billing_type) {
              const next = applyBillingSwitch(read(), changed.billing_type);
              form.setFieldsValue(next);
              sync(next);
              return;
            }
            sync(read());
          }}
        >
          <Section title={t("products.basic")}>
            <Form.Item label={t("products.name")} required className="mb-0">
              <Form.Item
                name={["name", I18N_DEFAULT]}
                className="mb-0"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("products.nameEmpty"),
                  },
                ]}
              >
                <CountedInput
                  max={50}
                  placeholder={t("products.textPh")}
                  suffix={globe(
                    live.name,
                    50,
                    (name) => applyI18n(["name"], name),
                    t("products.name"),
                    t("products.nameEmpty"),
                  )}
                />
              </Form.Item>
            </Form.Item>
            <Form.Item label={t("products.desc")} className="mb-0">
              <Form.Item name={["description", I18N_DEFAULT]} className="mb-0">
                <CountedInput
                  max={200}
                  textarea
                  placeholder={t("products.textPh")}
                  suffix={globe(
                    live.description,
                    200,
                    (description) => applyI18n(["description"], description),
                    t("products.desc"),
                  )}
                />
              </Form.Item>
            </Form.Item>
          </Section>

          <Section title={t("products.billingConfig")}>
            <Form.Item
              name="billing_type"
              label={t("products.billing")}
              required
              className="!mb-0"
            >
              <Radio.Group disabled={lockedBilling}>
                {BILLING_TYPES.map((key) => (
                  <Radio key={key} value={key}>
                    {t(BILLING_I18N[key])}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            {live.billing_type === BillingType.Free && (
              <div className="flex flex-col gap-2">
                <Alert
                  type="info"
                  showIcon
                  icon={<WarningIcon />}
                  title={t("products.freeHint")}
                  className={styles.alert}
                />
                <p className="m-0 text-[13px] text-tertiary">
                  {t("products.freeMarketHint")}
                </p>
              </div>
            )}

            {(live.billing_type === BillingType.Workspace ||
              live.billing_type === BillingType.Seat) && (
              <Form.Item
                name="planPrice"
                className={styles.formItem}
                required
                rules={[moneyRule(t("products.priceMin"))]}
                extra={t("products.priceHint")}
                label={
                  live.billing_type === BillingType.Seat
                    ? t("products.seatPrice")
                    : t("products.workspacePrice")
                }
              >
                <MoneyInput
                  suffix={
                    live.billing_type === BillingType.Seat
                      ? t("products.perSeatMonth")
                      : t("products.perMonth")
                  }
                />
              </Form.Item>
            )}

            {live.billing_type === BillingType.Usage && (
              <Form.Item
                label={t("products.tiers")}
                required
                className="!mb-0 !py-4 border-b border-t border-dashed border-line"
              >
                <Form.List name="tiers">
                  {(fields, { add, remove, move }, { errors }) => (
                    <div className="flex flex-col gap-3">
                      <SortableList
                        ids={fields.map((field) => field.key)}
                        onMove={(from, to) => {
                          move(from, to);
                          sync(read());
                        }}
                      >
                        {fields.map((field, i) => (
                          <SortableCard
                            key={field.key}
                            id={field.key}
                            title={t("products.tierIndex", { n: i + 1 })}
                            extra={
                              <Tooltip
                                title={
                                  fields.length <= 1
                                    ? t("products.keepOneTier")
                                    : undefined
                                }
                              >
                                <Button
                                  type="link"
                                  danger
                                  disabled={fields.length <= 1}
                                  onClick={() => remove(field.name)}
                                >
                                  {t("common.delete")}
                                </Button>
                              </Tooltip>
                            }
                          >
                            <Form.Item
                              label={t("products.tierName")}
                              required
                              className="!mb-0"
                            >
                              <Form.Item
                                name={[field.name, "name", I18N_DEFAULT]}
                                className="!mb-0"
                                rules={[
                                  {
                                    required: true,
                                    whitespace: true,
                                    message: t("products.tierNameEmpty"),
                                  },
                                  uniqueNameRule("tiers"),
                                ]}
                              >
                                <CountedInput
                                  max={50}
                                  placeholder={t("products.textPh")}
                                  suffix={globe(
                                    live.tiers[i]?.name ?? emptyI18n(),
                                    50,
                                    (name) =>
                                      applyI18n(["tiers", i, "name"], name),
                                    t("products.tierName"),
                                    t("products.tierNameEmpty"),
                                  )}
                                />
                              </Form.Item>
                            </Form.Item>
                            <Form.Item
                              label={t("products.tierDesc")}
                              className="!mb-0"
                            >
                              <Form.Item
                                name={[field.name, "description", I18N_DEFAULT]}
                                className="!mb-0"
                              >
                                <CountedInput
                                  max={200}
                                  textarea
                                  placeholder={t("products.textPh")}
                                  suffix={globe(
                                    live.tiers[i]?.description ?? emptyI18n(),
                                    200,
                                    (description) =>
                                      applyI18n(
                                        ["tiers", i, "description"],
                                        description,
                                      ),
                                    t("products.tierDesc"),
                                  )}
                                />
                              </Form.Item>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "price"]}
                              label={t("products.tierPrice")}
                              extra={t("products.usagePriceHint")}
                              required
                              rules={[moneyRule(t("products.tierPriceMin"))]}
                              className={styles.formItemPeriod}
                            >
                              <MoneyInput suffix={t("products.oneTime")} />
                            </Form.Item>
                            <Form.Item
                              label={t("products.expire")}
                              required
                              extra={t("products.expireHint")}
                              className={styles.formItemPeriod}
                            >
                              <div className="w-full flex gap-2 items-center">
                                <Form.Item
                                  name={[field.name, "expire_value"]}
                                  className="!mb-0 flex-1"
                                  rules={[
                                    {
                                      validator: (_, value: string) => {
                                        const n = Number(value);
                                        return value &&
                                          Number.isInteger(n) &&
                                          n >= 1
                                          ? Promise.resolve()
                                          : Promise.reject(
                                              new Error(
                                                t("products.expireMin"),
                                              ),
                                            );
                                      },
                                    },
                                  ]}
                                >
                                  <PositiveIntInput />
                                </Form.Item>
                                <Form.Item
                                  name={[field.name, "expire_unit"]}
                                  className="!mb-0"
                                >
                                  <Select
                                    className="!w-50"
                                    options={PERIOD_UNITS.map((unit) => ({
                                      value: unit,
                                      label: t(PERIOD_UNIT_LABEL_I18N[unit]),
                                    }))}
                                  />
                                </Form.Item>
                              </div>
                            </Form.Item>
                          </SortableCard>
                        ))}
                      </SortableList>
                      <Button
                        icon={<AddIcon />}
                        className="w-max"
                        onClick={() => add(emptyTier())}
                      >
                        {t("products.addTier")}
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            )}

            {live.billing_type === BillingType.Module && (
              <Form.Item
                label={t("products.modules")}
                required
                className="!mb-0 !py-4 border-b border-t border-dashed border-line"
              >
                <Form.List name="modules">
                  {(fields, { add, remove, move }, { errors }) => (
                    <div className="flex flex-col gap-3">
                      <SortableList
                        ids={fields.map((field) => field.key)}
                        onMove={(from, to) => {
                          move(from, to);
                          sync(read());
                        }}
                      >
                        {fields.map((field, i) => (
                          <SortableCard
                            key={field.key}
                            id={field.key}
                            title={t("products.moduleIndex", { n: i + 1 })}
                            extra={
                              <Tooltip
                                title={
                                  fields.length <= 1
                                    ? t("products.keepOneModule")
                                    : undefined
                                }
                              >
                                <Button
                                  type="link"
                                  danger
                                  disabled={fields.length <= 1}
                                  onClick={() => remove(field.name)}
                                >
                                  {t("common.delete")}
                                </Button>
                              </Tooltip>
                            }
                          >
                            <Form.Item
                              label={t("products.moduleName")}
                              required
                              className="!mb-0"
                            >
                              <Form.Item
                                name={[field.name, "name", I18N_DEFAULT]}
                                className="!mb-0"
                                rules={[
                                  {
                                    required: true,
                                    whitespace: true,
                                    message: t("products.moduleNameEmpty"),
                                  },
                                  uniqueNameRule("modules"),
                                ]}
                              >
                                <CountedInput
                                  max={50}
                                  placeholder={t("products.textPh")}
                                  suffix={globe(
                                    live.modules[i]?.name ?? emptyI18n(),
                                    50,
                                    (name) =>
                                      applyI18n(["modules", i, "name"], name),
                                    t("products.moduleName"),
                                    t("products.moduleNameEmpty"),
                                  )}
                                />
                              </Form.Item>
                            </Form.Item>
                            <Form.Item
                              label={t("products.moduleDesc")}
                              className="!mb-0"
                            >
                              <Form.Item
                                name={[field.name, "description", I18N_DEFAULT]}
                                className="!mb-0"
                              >
                                <CountedInput
                                  max={200}
                                  textarea
                                  placeholder={t("products.textPh")}
                                  suffix={globe(
                                    live.modules[i]?.description ?? emptyI18n(),
                                    200,
                                    (description) =>
                                      applyI18n(
                                        ["modules", i, "description"],
                                        description,
                                      ),
                                    t("products.moduleDesc"),
                                  )}
                                />
                              </Form.Item>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, "price"]}
                              label={t("products.monthPrice")}
                              extra={t("products.modulePriceHint")}
                              required
                              rules={[moneyRule(t("products.priceMin"))]}
                              className={styles.formItemPeriod}
                            >
                              <MoneyInput
                                suffix={t("products.perModuleMonth")}
                              />
                            </Form.Item>
                          </SortableCard>
                        ))}
                      </SortableList>
                      <Button
                        icon={<AddIcon />}
                        className="w-max"
                        onClick={() => add(emptyModule())}
                      >
                        {t("products.addModule")}
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            )}

            {showEffective && (
              <>
                <Form.Item
                  label={t("products.effectiveMode")}
                  required
                  className={cn(styles.formItem, styles.formItemEffective)}
                >
                  <Form.Item name="effective_mode" className="!mb-0">
                    <Radio.Group>
                      <Radio value={EffectiveMode.AfterExpiry}>
                        {t("products.afterExpiry")}
                      </Radio>
                      <Radio value={EffectiveMode.Immediate}>
                        {t("products.immediate")}
                      </Radio>
                      <Radio value={EffectiveMode.Scheduled}>
                        {t("products.scheduledMode")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {live.effective_mode === EffectiveMode.Scheduled && (
                    <Form.Item
                      name="effective_at"
                      className="!my-2"
                      rules={[
                        {
                          validator: (_, value: string | null) =>
                            isFutureUtc(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(t("products.effectiveAtFuture")),
                                ),
                        },
                      ]}
                    >
                      <UtcDatePicker
                        future
                        placeholder={t("products.effectiveAtPh")}
                      />
                    </Form.Item>
                  )}
                  <p className="mb-0 mt-1 text-[13px] text-tertiary">
                    {t("products.effectiveHint")}
                  </p>
                </Form.Item>
                <Form.Item label={t("products.autorenewal")} className="!mb-0">
                  <Input disabled value={t("products.autorenewalFixed")} />
                </Form.Item>
              </>
            )}
          </Section>

          {showPeriods && (
            <Section title={t("products.periodConfig")}>
              <Form.Item
                name="periods"
                extra={t("products.periodHint")}
                className={styles.formItemPeriod}
                rules={[
                  {
                    validator: (_, value: ProductPeriod[]) =>
                      value?.length
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("products.needPeriod"))),
                  },
                ]}
              >
                <PeriodTags
                  adding={addingPeriod}
                  removable={periodRemovable}
                  newLen={newLen}
                  newUnit={newUnit}
                  onAdding={setAddingPeriod}
                  onLen={setNewLen}
                  onUnit={setNewUnit}
                  onConfirm={addPeriod}
                  renderCard={periodCard}
                />
              </Form.Item>
              {live.billing_type === BillingType.Module &&
                sortedPeriods.length > 0 && (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="key"
                    dataSource={live.modules}
                    columns={[
                      {
                        title: t("products.moduleCol"),
                        render: (_, mod) =>
                          defaultI18n(mod.name) || t("products.dash"),
                      },
                      ...sortedPeriods.map((p, index) => ({
                        title: periodTitle(p.length, p.unit, "head"),
                        key: index,
                        render: (
                          _: unknown,
                          mod: FormState["modules"][number],
                        ) =>
                          moneyOk(mod.price)
                            ? formatUsd(periodPrice(Number(mod.price), p))
                            : t("products.dash"),
                      })),
                    ]}
                  />
                )}
              {live.billing_type === BillingType.Module &&
                sortedPeriods.length > 0 && (
                  <p className="m-0 text-xs text-[#8c8c8c]">
                    {t("products.modulePriceTable")}
                  </p>
                )}
            </Section>
          )}

          <Section title={t("products.listing")}>
            <Form.Item
              label={t("products.listMode")}
              required
              className="!mb-0 border-b border-dashed border-line !pb-4"
            >
              {listReadonly ? (
                <Form.Item name="list_at" className="!mb-0">
                  <UtcDatePicker
                    disabled
                    placeholder={t("products.listAtPh")}
                  />
                </Form.Item>
              ) : (
                <Space wrap>
                  <Form.Item
                    name="listNow"
                    className="!mb-0"
                    getValueFromEvent={(e) => e.target.value === ListMode.Now}
                    getValueProps={(value) => ({
                      value: value ? ListMode.Now : ListMode.At,
                    })}
                  >
                    <Radio.Group>
                      <Radio value={ListMode.Now}>
                        {t("products.listNowMode")}
                      </Radio>
                      <Radio value={ListMode.At}>
                        {t("products.listAtMode")}
                      </Radio>
                    </Radio.Group>
                  </Form.Item>
                  {!live.listNow && (
                    <Form.Item
                      name="list_at"
                      className="!mb-0"
                      rules={[
                        {
                          validator: (_, value: string | null) =>
                            isFutureUtc(value)
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(t("products.listAtFuture")),
                                ),
                        },
                      ]}
                    >
                      <UtcDatePicker
                        future
                        placeholder={t("products.listAtPh")}
                      />
                    </Form.Item>
                  )}
                </Space>
              )}
            </Form.Item>
            <Form.Item label={t("products.unlistMode")} className="!mb-0">
              <Space wrap>
                <Form.Item
                  name="unlistOn"
                  className="!mb-0"
                  getValueFromEvent={(e) => e.target.value === UnlistMode.At}
                  getValueProps={(value) => ({
                    value: value ? UnlistMode.At : UnlistMode.Never,
                  })}
                >
                  <Radio.Group>
                    <Radio value={UnlistMode.Never}>
                      {t("products.unlistNever")}
                    </Radio>
                    <Radio value={UnlistMode.At}>
                      {t("products.listAtMode")}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
                {live.unlistOn && (
                  <Form.Item
                    name="unlist_at"
                    className="!mb-0"
                    rules={[
                      {
                        validator: (_, value: string | null) => {
                          const listNow = form.getFieldValue("listNow");
                          const listed =
                            listNow || status === ProductStatus.Published;
                          const listAt = form.getFieldValue("list_at") as
                            | string
                            | null;
                          const listMs = listed
                            ? Date.now()
                            : Date.parse(listAt ?? "");
                          if (value && Date.parse(value) > listMs)
                            return Promise.resolve();
                          return Promise.reject(
                            new Error(
                              t(
                                listed
                                  ? "products.unlistAfterNow"
                                  : "products.unlistAfterList",
                              ),
                            ),
                          );
                        },
                      },
                    ]}
                  >
                    <UtcDatePicker
                      future={unlistAfterNow}
                      after={unlistAfterNow ? null : live.list_at}
                      placeholder={t("products.unlistAtPh")}
                    />
                  </Form.Item>
                )}
              </Space>
            </Form.Item>
          </Section>
        </Form>
      )}

      <I18nModal
        open={Boolean(i18nField)}
        max={i18nField?.max ?? 50}
        form={i18nForm}
        emptyMessage={i18nField?.emptyMessage}
        onCancel={() => setI18nField(null)}
        onApply={(next) => {
          i18nField?.apply(next);
          setI18nField(null);
        }}
      />
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-line p-5 bg-white">
      <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
        <span className="h-3.5 w-0.5 rounded-sm bg-accent" />
        {title}
      </h2>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

export default ProductEdit;
